import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/layout/Layout';
import { projectsAPI, clientsAPI, servicesAPI, invoicesAPI, quotesAPI, contractsAPI } from '../lib/api';
import { formatCurrency, getStatusLabel, getServiceTypeLabel } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Euro, Clock, Percent,
  FileText, CheckCircle, RefreshCw,
} from 'lucide-react';

const MONTH_NAMES = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const KpiCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, valueColor }) => (
  <Card className="p-5">
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg ${iconBg} shrink-0`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 uppercase tracking-wide leading-tight">{label}</p>
        <p className={`text-lg font-bold mt-0.5 ${valueColor || ''}`}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  </Card>
);

export const Report = () => {
  const [projects, setProjects]   = useState([]);
  const [clients, setClients]     = useState([]);
  const [services, setServices]   = useState([]);
  const [invoices, setInvoices]   = useState([]);
  const [quotes, setQuotes]       = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [period, setPeriod]       = useState(String(new Date().getFullYear()));

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pr, cl, sv, inv, qt, ct] = await Promise.all([
        projectsAPI.getAll(),
        clientsAPI.getAll(),
        servicesAPI.getAll(),
        invoicesAPI.getAll(),
        quotesAPI.getAll(),
        contractsAPI.getAll(),
      ]);
      setProjects(pr.data);
      setClients(cl.data);
      setServices(sv.data);
      setInvoices(inv.data);
      setQuotes(qt.data);
      setContracts(ct.data);
    } catch (e) {
      console.error('Report fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.nome || 'N/A';

  const inPeriod = (dateStr) => {
    if (period === 'all' || !dateStr) return period === 'all';
    return new Date(dateStr).getFullYear() === parseInt(period);
  };

  // ── Fatturazione ──────────────────────────────────────────────────────────────
  const paidInvoices    = invoices.filter(i => i.stato === 'pagata' && inPeriod(i.data_pagamento || i.created_at));
  const pendingInvoices = invoices.filter(i => ['emessa', 'da_emettere'].includes(i.stato) && inPeriod(i.data_scadenza || i.created_at));
  const incassato       = paidInvoices.reduce((s, i) => s + (i.importo || 0), 0);
  const daIncassare     = pendingInvoices.reduce((s, i) => s + (i.importo || 0), 0);

  // ── Contratti MRR/ARR ─────────────────────────────────────────────────────────
  const toMonthly = (c) => {
    const v = c.valore || 0;
    if (c.ricorrenza === 'mensile')     return v;
    if (c.ricorrenza === 'trimestrale') return v / 3;
    if (c.ricorrenza === 'semestrale')  return v / 6;
    if (c.ricorrenza === 'annuale')     return v / 12;
    return 0;
  };
  const activeRecurring = contracts.filter(c => c.stato === 'attivo' && c.ricorrenza);
  const mrr = activeRecurring.reduce((s, c) => s + toMonthly(c), 0);
  const arr = mrr * 12;

  // Contratti con costo acquisto (margine)
  const contractsWithCosto = contracts.filter(c => c.stato === 'attivo' && (c.costo_acquisto || 0) > 0);
  const margineContratti   = contractsWithCosto.reduce((s, c) => s + (c.valore || 0) - (c.costo_acquisto || 0), 0);

  // ── Preventivi ────────────────────────────────────────────────────────────────
  const filteredQuotes  = quotes.filter(q => inPeriod(q.created_at));
  const qTotal          = filteredQuotes.length;
  const qApproved       = filteredQuotes.filter(q => q.stato === 'approvato').length;
  const qPending        = filteredQuotes.filter(q => q.stato === 'inviato').length;
  const qRejected       = filteredQuotes.filter(q => q.stato === 'rifiutato').length;
  const convRate        = qTotal > 0 ? Math.round((qApproved / qTotal) * 100) : 0;
  const quotePieData    = [
    { name: 'Approvati', value: qApproved,  color: '#10b981' },
    { name: 'In attesa', value: qPending,   color: '#f59e0b' },
    { name: 'Rifiutati', value: qRejected,  color: '#ef4444' },
  ].filter(d => d.value > 0);

  // ── Fatturato mensile ─────────────────────────────────────────────────────────
  const monthlyRevenue = useMemo(() => {
    const months = {};
    invoices.forEach(inv => {
      const raw = inv.data_pagamento || inv.created_at;
      if (!raw) return;
      const d = new Date(raw);
      if (period !== 'all' && d.getFullYear() !== parseInt(period)) return;
      const m = d.getMonth();
      if (!months[m]) months[m] = { mese: MONTH_NAMES[m], incassato: 0, emesso: 0 };
      if (inv.stato === 'pagata') months[m].incassato += inv.importo || 0;
      else months[m].emesso += inv.importo || 0;
    });
    return Object.entries(months).sort(([a], [b]) => a - b).map(([, v]) => v);
  }, [invoices, period]);

  // ── Progetti ──────────────────────────────────────────────────────────────────
  const totalBudget      = projects.reduce((s, p) => s + (p.budget || 0), 0);
  const totalCost        = projects.reduce((s, p) => s + (p.costo_totale || 0), 0);
  const totalMargin      = totalBudget - totalCost;
  const marginPct        = totalBudget > 0 ? ((totalMargin / totalBudget) * 100).toFixed(1) : '0';
  const totalHours       = projects.reduce((s, p) => s + (p.ore_totali || 0), 0);
  const activeProjects   = projects.filter(p => p.stato === 'in_corso');
  const completedProjects= projects.filter(p => p.stato === 'completato');

  const projectProfitability = projects
    .filter(p => p.budget > 0)
    .map(p => ({
      nome:       p.nome,
      cliente:    getClientName(p.client_id),
      budget:     p.budget,
      costo:      p.costo_totale || 0,
      margine:    p.budget - (p.costo_totale || 0),
      percentuale:((p.budget - (p.costo_totale || 0)) / p.budget * 100).toFixed(1),
      ore:        p.ore_totali || 0,
      stato:      p.stato,
    }))
    .sort((a, b) => b.margine - a.margine);

  // ── Top clienti ───────────────────────────────────────────────────────────────
  const clientStats = {};
  projects.forEach(p => {
    if (!clientStats[p.client_id]) clientStats[p.client_id] = { budget: 0, costo: 0, progetti: 0 };
    clientStats[p.client_id].budget   += p.budget || 0;
    clientStats[p.client_id].costo    += p.costo_totale || 0;
    clientStats[p.client_id].progetti += 1;
  });
  const topClients = Object.entries(clientStats)
    .map(([id, s]) => ({
      cliente:  getClientName(id),
      budget:   s.budget,
      margine:  s.budget - s.costo,
      progetti: s.progetti,
    }))
    .sort((a, b) => b.budget - a.budget)
    .slice(0, 5);

  // ── Servizi ───────────────────────────────────────────────────────────────────
  const serviceStats = {};
  projects.forEach(p => {
    const servizi = p.servizi || [];
    const bPer = (p.budget || 0) / Math.max(servizi.length, 1);
    const cPer = (p.costo_totale || 0) / Math.max(servizi.length, 1);
    servizi.forEach(sid => {
      const tipo = services.find(s => s.id === sid)?.tipo || 'altro';
      if (!serviceStats[tipo]) serviceStats[tipo] = { budget: 0, costo: 0 };
      serviceStats[tipo].budget += bPer;
      serviceStats[tipo].costo  += cPer;
    });
  });
  const serviceChartData = Object.entries(serviceStats).map(([tipo, s]) => ({
    name:    getServiceTypeLabel(tipo),
    budget:  Math.round(s.budget),
    margine: Math.round(s.budget - s.costo),
  }));

  const currentYear = new Date().getFullYear();

  if (loading) {
    return (
      <Layout title="Report">
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      </Layout>
    );
  }

  return (
    <Layout title="Report">
      <div className="space-y-8">

        {/* Filtro periodo */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 font-medium">Periodo:</span>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={String(currentYear)}>Anno corrente ({currentYear})</SelectItem>
              <SelectItem value={String(currentYear - 1)}>{currentYear - 1}</SelectItem>
              <SelectItem value={String(currentYear - 2)}>{currentYear - 2}</SelectItem>
              <SelectItem value="all">Tutto il periodo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ── Sezione: Fatturazione ── */}
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Fatturazione</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={CheckCircle} iconBg="bg-emerald-100" iconColor="text-emerald-600"
              label="Incassato" value={formatCurrency(incassato)} valueColor="text-emerald-600"
              sub={`${paidInvoices.length} fatture pagate`}
            />
            <KpiCard
              icon={FileText} iconBg="bg-amber-100" iconColor="text-amber-600"
              label="Da incassare" value={formatCurrency(daIncassare)} valueColor="text-amber-600"
              sub={`${pendingInvoices.length} fatture aperte`}
            />
            <KpiCard
              icon={RefreshCw} iconBg="bg-indigo-100" iconColor="text-indigo-600"
              label="MRR contratti" value={formatCurrency(mrr)}
              sub={`${activeRecurring.length} contratti ricorrenti`}
            />
            <KpiCard
              icon={TrendingUp} iconBg="bg-purple-100" iconColor="text-purple-600"
              label="ARR contratti" value={formatCurrency(arr)}
              sub="MRR × 12"
            />
          </div>
        </section>

        {/* ── Sezione: Progetti ── */}
        <section>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Progetti</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={Euro} iconBg="bg-indigo-100" iconColor="text-indigo-600"
              label="Budget totale" value={formatCurrency(totalBudget)}
              sub={`${projects.length} progetti`}
            />
            <KpiCard
              icon={Clock} iconBg="bg-amber-100" iconColor="text-amber-600"
              label="Costo ore" value={formatCurrency(totalCost)}
              sub={`${totalHours.toFixed(0)}h registrate`}
            />
            <KpiCard
              icon={totalMargin >= 0 ? TrendingUp : TrendingDown}
              iconBg={totalMargin >= 0 ? 'bg-emerald-100' : 'bg-red-100'}
              iconColor={totalMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}
              label="Margine progetti" value={formatCurrency(totalMargin)}
              valueColor={totalMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}
              sub={`${activeProjects.length} in corso · ${completedProjects.length} completati`}
            />
            <KpiCard
              icon={Percent} iconBg="bg-purple-100" iconColor="text-purple-600"
              label="Margine %" value={`${marginPct}%`}
              valueColor={parseFloat(marginPct) >= 0 ? 'text-emerald-600' : 'text-red-600'}
              sub="Budget vs costo ore"
            />
          </div>
        </section>

        {/* ── Charts row 1: Andamento + Preventivi ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Andamento Fatturato</CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyRevenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={monthlyRevenue} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="mese" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(v, n) => [formatCurrency(v), n === 'incassato' ? 'Incassato' : 'Emesso']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend formatter={(v) => v === 'incassato' ? 'Incassato' : 'Emesso/da incassare'} />
                    <Bar dataKey="incassato" name="incassato" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="emesso"    name="emesso"    fill="#4338ca" opacity={0.4} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">
                  Nessuna fattura nel periodo selezionato
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conversione Preventivi</CardTitle>
            </CardHeader>
            <CardContent>
              {qTotal > 0 ? (
                <div className="space-y-5">
                  <div className="flex items-center justify-center">
                    <div className="relative w-[160px] h-[160px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={quotePieData} cx="50%" cy="50%" innerRadius={52} outerRadius={75} dataKey="value" strokeWidth={2}>
                            {quotePieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <p className="text-2xl font-bold text-slate-800">{convRate}%</p>
                        <p className="text-xs text-slate-400">conv.</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {quotePieData.map((d, i) => (
                      <div key={i} className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                          <span className="text-slate-600">{d.name}</span>
                        </div>
                        <span className="font-semibold">{d.value}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-1.5 border-t border-slate-100">
                      <span className="text-slate-400">Totale preventivi</span>
                      <span className="font-semibold">{qTotal}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">
                  Nessun preventivo nel periodo
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Charts row 2: Clienti + Servizi ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top 5 Clienti</CardTitle>
            </CardHeader>
            <CardContent>
              {topClients.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={topClients} layout="vertical" barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="cliente" tick={{ fontSize: 11 }} stroke="#9ca3af" width={90} />
                    <Tooltip
                      formatter={(v, n) => [formatCurrency(v), n === 'budget' ? 'Budget' : 'Margine']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend formatter={(v) => v === 'budget' ? 'Budget' : 'Margine'} />
                    <Bar dataKey="budget"  name="budget"  fill="#4338ca" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="margine" name="margine" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">Nessun dato</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Budget per Tipo di Servizio</CardTitle>
            </CardHeader>
            <CardContent>
              {serviceChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={serviceChartData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(v, n) => [formatCurrency(v), n === 'budget' ? 'Budget' : 'Margine']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend formatter={(v) => v === 'budget' ? 'Budget' : 'Margine'} />
                    <Bar dataKey="budget"  name="budget"  fill="#4338ca" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="margine" name="margine" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">Nessun dato</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Tabella Redditività Progetti ── */}
        <Card data-testid="projects-profitability-table">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base">Redditività Progetti</CardTitle>
              <div className="flex gap-4 text-sm text-slate-500">
                <span><span className="font-semibold text-blue-600">{activeProjects.length}</span> in corso</span>
                <span><span className="font-semibold text-emerald-600">{completedProjects.length}</span> completati</span>
                <span><span className="font-semibold">{totalHours.toFixed(0)}h</span> totali</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Progetto</TableHead>
                    <TableHead className="hidden md:table-cell">Cliente</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead className="text-right">Margine</TableHead>
                    <TableHead className="text-right">%</TableHead>
                    <TableHead className="text-right hidden lg:table-cell">Ore</TableHead>
                    <TableHead className="hidden lg:table-cell">Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectProfitability.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                        Nessun progetto con budget definito
                      </TableCell>
                    </TableRow>
                  ) : projectProfitability.map((p, i) => (
                    <TableRow key={i} className="table-row-hover">
                      <TableCell className="font-medium">{p.nome}</TableCell>
                      <TableCell className="hidden md:table-cell text-slate-500">{p.cliente}</TableCell>
                      <TableCell className="text-right currency">{formatCurrency(p.budget)}</TableCell>
                      <TableCell className="text-right text-slate-500 currency">{formatCurrency(p.costo)}</TableCell>
                      <TableCell className={`text-right font-medium currency ${p.margine >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {p.margine >= 0 ? '+' : ''}{formatCurrency(p.margine)}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${parseFloat(p.percentuale) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {p.percentuale}%
                      </TableCell>
                      <TableCell className="text-right hidden lg:table-cell text-slate-500">{p.ore}h</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Badge variant="outline">{getStatusLabel(p.stato)}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* ── Tabella Margine Contratti (solo se ci sono dati) ── */}
        {contractsWithCosto.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Margine Contratti Attivi</CardTitle>
                <div className="flex gap-4 text-sm text-slate-500">
                  <span>Costo acq.: <span className="font-semibold">{formatCurrency(contractsWithCosto.reduce((s, c) => s + (c.costo_acquisto || 0), 0))}</span></span>
                  <span className={`font-semibold ${margineContratti >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    Margine: {formatCurrency(margineContratti)}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead>Contratto</TableHead>
                      <TableHead className="hidden md:table-cell">Cliente</TableHead>
                      <TableHead className="text-right">Valore</TableHead>
                      <TableHead className="text-right">Costo acq.</TableHead>
                      <TableHead className="text-right">Margine</TableHead>
                      <TableHead className="text-right">%</TableHead>
                      <TableHead className="hidden md:table-cell">Ricorrenza</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contractsWithCosto
                      .sort((a, b) => ((b.valore || 0) - (b.costo_acquisto || 0)) - ((a.valore || 0) - (a.costo_acquisto || 0)))
                      .map(c => {
                        const margine = (c.valore || 0) - (c.costo_acquisto || 0);
                        const pct = c.valore > 0 ? ((margine / c.valore) * 100).toFixed(1) : '—';
                        return (
                          <TableRow key={c.id} className="table-row-hover">
                            <TableCell className="font-medium">{c.titolo}</TableCell>
                            <TableCell className="hidden md:table-cell text-slate-500">{getClientName(c.client_id)}</TableCell>
                            <TableCell className="text-right currency">{formatCurrency(c.valore)}</TableCell>
                            <TableCell className="text-right text-slate-500 currency">{formatCurrency(c.costo_acquisto)}</TableCell>
                            <TableCell className={`text-right font-medium currency ${margine >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {margine >= 0 ? '+' : ''}{formatCurrency(margine)}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${parseFloat(pct) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {pct}%
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-slate-500 capitalize">
                              {c.ricorrenza || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </Layout>
  );
};
